import React, { useState } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { GlobalFonts } from '../../styles/GlobalFonts';
import { threeTheoryLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin: 50px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #0180ff;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 42px;
  margin-bottom: 15px;
  margin-top: min(1px, 0.2vh);
  line-height: 80%;
  font-weight: 600;
  text-align: center;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin: 0 10px;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  align-self: stretch;
  text-align: left;
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonText = styled.span`
  flex: 1;
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
`;

const ButtonIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: fill;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #0180ff;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 20px;
  margin: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  color: #ffffff;
  margin: 0 0 36px 0;
  text-align: center;
  white-space: pre-line;
  line-height: 90%;
  letter-spacing: -0.02em;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0;
  white-space: pre-line;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const modalVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      delay: 0.1,
    },
  },
};

const modalContentVariants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

const ThreeTheory4: React.FC<{
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
}> = ({ onSwipeLeft, onSwipeRight, toHome }) => {
  const [isAPRModalOpen, setIsAPRModalOpen] = useState(false);
  const [isTVLModalOpen, setIsTVLModalOpen] = useState(false);
  const [isVolumeModalOpen, setIsVolumeModalOpen] = useState(false);

  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = threeTheoryLocales[lang][4];
  const buttons = t.buttons;
  const modals = t.modals;

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ScrollContainer>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIcon.svg" />
          </HomeButton>
          <Header>
            <Title>{t.title}</Title>
            <TheoryButton>{t.theory}</TheoryButton>
          </Header>
          <QuestionBlock>
            <QuestionText>{t.question}</QuestionText>
          </QuestionBlock>
          <Description>{t.description}</Description>
          <ButtonContainer>
            <StyledButton onClick={() => setIsAPRModalOpen(true)}>
              <ButtonText>{buttons.apr}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton onClick={() => setIsTVLModalOpen(true)}>
              <ButtonText>{buttons.tvl}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton onClick={() => setIsVolumeModalOpen(true)}>
              <ButtonText>{buttons.volume}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>
          </ButtonContainer>

          <AnimatePresence>
            {isAPRModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsAPRModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton onClick={() => setIsAPRModalOpen(false)} />
                  <ModalTitle>{modals.apr.title}</ModalTitle>
                  <ModalText>{modals.apr.text}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}

            {isTVLModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsTVLModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton onClick={() => setIsTVLModalOpen(false)} />
                  <ModalTitle>{modals.tvl.title}</ModalTitle>
                  <ModalText>{modals.tvl.text}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}

            {isVolumeModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsVolumeModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton onClick={() => setIsVolumeModalOpen(false)} />
                  <ModalTitle>{modals.volume.title}</ModalTitle>
                  <ModalText>{modals.volume.text}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>

          <Footer
            textLeft={t.back}
            textRight={t.next}
            onSwipeLeft={onSwipeLeft}
            onSwipeRight={onSwipeRight}
          ></Footer>
        </ScrollContainer>
      </Container>
    </div>
  );
};

export default ThreeTheory4;
